import React from 'react';
import { Edit, CalendarPlus } from 'lucide-react';
import Button from '@components/common/Button';
import MeetingTooltip from '@components/common/MeetingTooltip';
import { ScheduleItem } from '@app-types';
import { formatMediumDateTime } from '@utils/dateUtils';
import { hasPendingDataEnrichment } from './MultiRoleCell';
import styles from './SchedulerTableCells.module.css';

const MeetingDateCell: React.FC<{
  scheduleItem: ScheduleItem;
  onScheduleDate: (item: ScheduleItem) => void;
  canScheduleMeeting: (item: ScheduleItem) => boolean;
  isChildRow?: boolean;
  isGroupRow?: boolean;
}> = ({ scheduleItem, onScheduleDate, canScheduleMeeting, isChildRow, isGroupRow }) => {
  // For child rows, show left-aligned dash
  if (isChildRow) {
    return <span className={styles.emptyCell}>—</span>;
  }

  // For group rows with pending_data_enrichment
  if (isGroupRow && hasPendingDataEnrichment(scheduleItem)) {
    return <span className={styles.noDate}>Not scheduled</span>;
  }

  // Booked (scheduled/rescheduled) meetings stay editable so the Scheduler can reschedule them (ADO-1147).
  const showButton = canScheduleMeeting(scheduleItem) && !isChildRow && scheduleItem.meeting?.status !== 'completed';

  // Prepare minimal data for tooltip (only what's actually used)
  const getMeetingForTooltip = () => {
    if (!scheduleItem.meeting?.invites) return null;

    // Return meeting with invites for tooltip to extract statuses
    return {
      invites: scheduleItem.meeting.invites
    };
  };

  // Prepare contracts data for tooltip - only assessor and contract managers
  const getContractsForTooltip = () => {
    // Managers are the same across all contracts, use the first contract
    const managers =
      scheduleItem.contracts[0]?.contract_managers.map((manager) => ({
        id: manager.id,
        name: manager.name
      })) || [];

    return [
      {
        assessor_name: scheduleItem.assessor?.name,
        assessor_id: scheduleItem.assessor?.id,
        contract_managers: managers
      }
    ];
  };

  const meetingData = getMeetingForTooltip();
  const contractsData = getContractsForTooltip();

  return (
    <div className={styles.meetingDateCell}>
      <div className={styles.roleAssignment}>
        <span className={styles.roleName}>
          {scheduleItem.meeting?.scheduled_datetime && meetingData ? (
            <MeetingTooltip meeting={meetingData} contracts={contractsData}>
              <span className={styles.meetingDate}>{formatMediumDateTime(scheduleItem.meeting.scheduled_datetime)}</span>
            </MeetingTooltip>
          ) : (
            <span className={styles.noDate}>Not scheduled</span>
          )}
        </span>
        {showButton && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onScheduleDate(scheduleItem)}
            className={styles.roleAssignBtn}
            data-test-id="role-assign-btn-meeting-date"
          >
            {scheduleItem.meeting?.scheduled_datetime ? <Edit size={14} /> : <CalendarPlus size={14} />}
          </Button>
        )}
      </div>
    </div>
  );
};

export default MeetingDateCell;
